/**
 * The t=0 instant preview (docs/ORG_TENANCY_SPEC.md upload UX): the
 * browser holds the COMPLETE file before the server sees a byte, so
 * web-ifc meshes it locally and the building renders -- neutral gray,
 * visibly provisional. Gray = local preview, colored = server truth:
 * every pixel is honest about which parser produced it.
 *
 * Meshing runs in a WORKER (meshWorker.ts) and the scene builds
 * PROGRESSIVELY as meshes stream across -- the main thread never
 * blocks, so there is no size limit (the 25MB gate died with the
 * main-thread mesher). web-ifc is NEVER the authoritative mesher
 * (BRep robustness) -- ifcopenshell keeps the ingest path; if the
 * worker fails to parse, the preview is skipped and the websocket
 * flow alone is the UX (degrade path: onFailed).
 */
import { useEffect, useRef, useState } from 'react';
import { usePalette } from '../../../theme';
import { ActionButton, Overlay, Text } from '../../../strata';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { MeshWorkerOut } from '../meshWorker';

// Reframe cadence: often enough that the model grows into view while
// streaming, rare enough that the camera isn't constantly yanked.
const REFRAME_EVERY = 250;

interface IfcPreviewProps {
  file: File;
  onFailed: (reason: string) => void;
  /** The shell's close: server truth is one click away while the preview covers it. */
  onClose: () => void;
}

export function IfcPreview({ file, onFailed, onClose }: IfcPreviewProps) {
  const palette = usePalette();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [meshCount, setMeshCount] = useState<number | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let disposed = false;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(palette.canvasGround);
    const camera = new THREE.PerspectiveCamera(50, 4 / 3, 0.1, 1e6);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    const size = () => {
      const width = mount.clientWidth || 640;
      const height = mount.clientHeight || 420;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    size();
    // The preview fills whatever slot the shell gives it (the main
    // canvas since 2026-07-27) -- track the slot, not the window.
    const resizeObserver = new ResizeObserver(size);
    resizeObserver.observe(mount);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(1, 2, 1.5);
    scene.add(sun);

    // No axis rotation: web-ifc's flatTransformation already bakes the
    // IFC z-up -> three y-up conversion. A -90-degree root rotation on
    // top of it DOUBLE-converts and pitches the building onto its face
    // (seen live on bunkhouse; the eng models' piping had no obvious
    // "up" so it went unnoticed).
    const root = new THREE.Group();
    scene.add(root);

    const controls = new OrbitControls(camera, renderer.domElement);
    const material = new THREE.MeshLambertMaterial({
      color: palette.canvasProvisional, // neutral gray: visibly provisional
      side: THREE.DoubleSide,
    });

    let frame = 0;
    const animate = () => {
      if (disposed) return;
      frame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate(); // render from t=0: the scene GROWS while meshes stream

    const reframe = () => {
      // Compose EVERY descendant's matrixWorld first: the meshes are
      // matrixAutoUpdate=false and Box3.setFromObject reads matrixWorld
      // without composing children -- stale identity matrices framed
      // the ORIGIN while bunkhouse's real site coordinates put the
      // building 460m away (found live: a black canvas with 7,251
      // healthy meshes; eng_v2 only ever looked right because its
      // transforms are near-identity).
      root.updateWorldMatrix(true, true);
      const bounds = new THREE.Box3().setFromObject(root);
      if (bounds.isEmpty()) return;
      const center = bounds.getCenter(new THREE.Vector3());
      const extent = bounds.getSize(new THREE.Vector3()).length() || 10;
      controls.target.copy(center);
      camera.position.copy(center).add(new THREE.Vector3(extent * 0.6, extent * 0.5, extent * 0.6));
      camera.near = extent / 1000;
      camera.far = extent * 10;
      camera.updateProjectionMatrix();
    };

    const worker = new Worker(new URL('../meshWorker.ts', import.meta.url), { type: 'module' });
    let count = 0;
    worker.onmessage = (event: MessageEvent<MeshWorkerOut>) => {
      if (disposed) return;
      const message = event.data;
      if (message.kind === 'mesh') {
        // web-ifc interleaves position+normal (6 floats per vertex).
        const buffer = new THREE.BufferGeometry();
        const interleaved = new THREE.InterleavedBuffer(message.verts, 6);
        buffer.setAttribute('position', new THREE.InterleavedBufferAttribute(interleaved, 3, 0));
        buffer.setAttribute('normal', new THREE.InterleavedBufferAttribute(interleaved, 3, 3));
        buffer.setIndex(new THREE.BufferAttribute(message.indices, 1));
        const mesh = new THREE.Mesh(buffer, material);
        mesh.matrix.fromArray(message.matrix);
        mesh.matrixAutoUpdate = false;
        root.add(mesh);
        count++;
        if (count === 1 || count % REFRAME_EVERY === 0) {
          reframe();
          setMeshCount(count);
        }
      } else if (message.kind === 'done') {
        reframe();
        setMeshCount(message.count);
      } else {
        onFailed(message.reason);
      }
    };
    worker.onerror = (event) => {
      if (!disposed) onFailed(event.message || 'mesh worker crashed');
    };
    void file
      .arrayBuffer()
      .then((buffer) => {
        if (!disposed) worker.postMessage({ buffer }, [buffer]);
      })
      .catch((err) => {
        if (!disposed) onFailed(err instanceof Error ? err.message : String(err));
      });

    return () => {
      disposed = true;
      worker.terminate();
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh) obj.geometry.dispose();
      });
      material.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, [file, onFailed, palette]);

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 5, background: palette.canvasGround }}>
      <Overlay anchor="top-left" dense>
        <Text size="caption" tone="secondary">
          Instant preview (local, provisional{meshCount !== null ? ` · ${meshCount.toLocaleString()} meshes` : ' · meshing…'})
        </Text>
      </Overlay>
      <Overlay anchor="top-right" dense>
        <ActionButton size="sm" onPress={onClose}>
          Close preview
        </ActionButton>
      </Overlay>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
